import { Component, Input, OnInit } from '@angular/core';
import { NbDialogRef } from '@nebular/theme';
import {
  GuardrailAction,
  GuardrailEnforce,
  GuardrailRule,
  GuardrailRuleInput,
  GuardrailSeverity,
} from '../../../services/guardrail.service';

/**
 * Create / edit dialog for one operator guardrail rule.
 *
 * Used from both scopes — the organisation page and the tutor page — with
 * `scope` only driving the copy, not the shape of the form.
 *
 * The field set deliberately mirrors Amazon Bedrock's denied-topic shape
 * (name + natural-language definition + up to five examples): the classifier
 * behind it can later be swapped for a hosted one without changing this form.
 */
@Component({
  selector: 'app-guardrail-rule-dialog',
  templateUrl: './guardrail-rule-dialog.component.html',
  styleUrl: './guardrail-rule-dialog.component.scss',
})
export class GuardrailRuleDialogComponent implements OnInit {
  @Input() rule: GuardrailRule | null = null;
  @Input() scope: 'client' | 'bot' = 'client';
  @Input() onConfirm: (input: GuardrailRuleInput) => void = () => {};

  /** Backend `endpoints.code_reference` values — never translated, they are
   *  the mapping keys the graph layer routes on. */
  readonly templateOptions = [
    'alpha_tutor',
    'alpha_flexible',
    'alpha_dozent',
    'alpha_assist',
    'alpha_support',
  ];

  readonly maxExamples = 5;

  model: GuardrailRuleInput = {
    name: '',
    definition: '',
    examples: [],
    enforce: 'hard',
    // New rules start in observation mode on purpose: the operator sees the
    // real hit rate before a single learner is ever blocked.
    action: 'log_only',
    redirect_hint: '',
    severity: 'medium',
    applies_to_templates: null,
    enabled: true,
  };

  /** Bound to the textarea — one example per line, joined on submit. */
  examplesText = '';
  allTemplates = true;

  constructor(protected dialogRef: NbDialogRef<GuardrailRuleDialogComponent>) {}

  ngOnInit() {
    if (this.rule) {
      this.model = {
        name: this.rule.name,
        definition: this.rule.definition,
        examples: [...(this.rule.examples || [])],
        enforce: this.rule.enforce,
        action: this.rule.action,
        redirect_hint: this.rule.redirect_hint || '',
        severity: this.rule.severity,
        applies_to_templates: this.rule.applies_to_templates
          ? [...this.rule.applies_to_templates]
          : null,
        enabled: this.rule.enabled,
      };
      this.examplesText = (this.rule.examples || []).join('\n');
      this.allTemplates = !this.rule.applies_to_templates?.length;
    }
  }

  get isEdit(): boolean {
    return !!this.rule?.id;
  }

  get parsedExamples(): string[] {
    return this.examplesText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => !!line)
      .slice(0, this.maxExamples);
  }

  get exampleCount(): number {
    return this.parsedExamples.length;
  }

  get tooManyExamples(): boolean {
    return (
      this.examplesText.split('\n').filter((l) => !!l.trim()).length >
      this.maxExamples
    );
  }

  onEnforceChange(value: GuardrailEnforce) {
    this.model.enforce = value;
  }

  onActionChange(value: GuardrailAction) {
    this.model.action = value;
  }

  onSeverityChange(value: GuardrailSeverity) {
    this.model.severity = value;
  }

  onAllTemplatesChange(checked: boolean) {
    this.allTemplates = checked;
    if (checked) {
      this.model.applies_to_templates = null;
    } else if (!this.model.applies_to_templates) {
      this.model.applies_to_templates = [];
    }
  }

  onTemplatesChange(values: string[]) {
    this.model.applies_to_templates = values?.length ? values : [];
  }

  get canSave(): boolean {
    if (!this.model.name?.trim() || !this.model.definition?.trim()) {
      return false;
    }
    // Mirrors the DB CHECK — a redirect without an instruction has nothing
    // to inject into the turn.
    if (this.model.action === 'redirect' && !this.model.redirect_hint?.trim()) {
      return false;
    }
    return true;
  }

  submit() {
    if (!this.canSave) return;
    this.onConfirm({
      ...this.model,
      name: this.model.name.trim(),
      definition: this.model.definition.trim(),
      examples: this.parsedExamples,
      redirect_hint:
        this.model.action === 'redirect'
          ? (this.model.redirect_hint || '').trim()
          : null,
      applies_to_templates: this.allTemplates
        ? null
        : this.model.applies_to_templates?.length
          ? this.model.applies_to_templates
          : null,
    });
    this.dialogRef.close();
  }

  close() {
    this.dialogRef.close();
  }
}
